import { debounce } from "@mui/material";
import { useEffect, useRef } from "react";
import type { RefObject } from "react";

interface UseElementResizeAndViewportOptions {
  debounceMs?: number;
  callback: (isOverflowViewport: boolean, e: ResizeObserverEntry) => void;
  triggerOnce?: boolean;
}

export const useElementResizeAndViewportOverflow = <TElement extends HTMLElement>(
  options: UseElementResizeAndViewportOptions
): RefObject<TElement> => {
  const { debounceMs = 100, callback, triggerOnce = false } = options;
  const elementRef = useRef<TElement>(null);
  const lastOverflowState = useRef<boolean>(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new ResizeObserver(([iOEntry]) => {
      const viewportHeight = window.visualViewport?.height ?? document.documentElement.clientHeight;
      const isOverflowViewport = iOEntry.target.getBoundingClientRect().bottom > viewportHeight;
      debounce(() => {
        if (isOverflowViewport !== lastOverflowState.current) {
          lastOverflowState.current = isOverflowViewport;
          callback(isOverflowViewport, iOEntry);
          if (triggerOnce && isOverflowViewport) {
            observer.unobserve(element);
          }
        }
      }, debounceMs)();
    });

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [triggerOnce, callback, debounceMs]);

  return elementRef;
};
